import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EEnvironment, ENVIRONMENT } from '@redngapps/shared/env/types';
import { Nullable } from '@redngapps/shared/types';
import { WINDOW } from '@redngapps/shared/util';
import { isNil } from 'lodash-es';
import { catchError, map, Observable, of, tap, throwError } from 'rxjs';

import { IAuthProvider } from './auth-provider.interface';
import { IAuthSession } from './auth-session.interface';

const DEV_EMAIL = 'dev.nutzer@redmedical.de';
const DEV_NAME = 'Dev Nutzer';
// One of the service's ALLOWED_WRITE_GROUPS; override via localStorage to simulate other permissions.
const DEV_GROUPS_DEFAULT = 'BetaReadWrite';
const DEV_GROUPS_STORAGE_KEY = 'feature-flags-dev-groups';
const DEV_TOKEN_PATH = '/service/dev/token';
const DEV_SESSION_STORAGE_KEY = 'feature-flags-dev-session';

interface IDevTokenResponse {
  token: string;
}

/**
 * Local-development auth provider: `GET /service/dev/token` issues a short-lived bearer the guard
 * accepts like a real Google `id_token` (`AUTH_MODE=dev`). Never reachable in a deployed
 * environment — selection is gated on `environment.name === development`.
 */
@Injectable({ providedIn: 'root' })
export class DevTokenAuthProvider implements IAuthProvider {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(ENVIRONMENT);
  private readonly window = inject(WINDOW);

  login(): Observable<IAuthSession> {
    return this.issueSession().pipe(tap(() => this.sessionStore?.setItem(DEV_SESSION_STORAGE_KEY, 'true')));
  }

  logout(): Observable<void> {
    this.sessionStore?.removeItem(DEV_SESSION_STORAGE_KEY);
    return of(undefined);
  }

  restoreSession(): Observable<Nullable<IAuthSession>> {
    if (isNil(this.sessionStore?.getItem(DEV_SESSION_STORAGE_KEY))) {
      return of(null);
    }

    // Re-issue a fresh token; a backend that is down must not break bootstrap (APP_INITIALIZER).
    return this.issueSession().pipe(catchError(() => of(null)));
  }

  private issueSession(): Observable<IAuthSession> {
    if (this.environment.name !== EEnvironment.development) {
      // Defense-in-depth: never mint a session if injected directly in a deployed environment.
      return throwError(() => new Error('DevTokenAuthProvider is only available in local development'));
    }

    const groups = this.localStore?.getItem(DEV_GROUPS_STORAGE_KEY) ?? DEV_GROUPS_DEFAULT;
    return this.http
      .get<IDevTokenResponse>(DEV_TOKEN_PATH, { params: { email: DEV_EMAIL, groups } })
      .pipe(map(response => ({ accessToken: response.token, user: { name: DEV_NAME, email: DEV_EMAIL } })));
  }

  // Browser storage via the WINDOW token; null only without a DOM (never in dev/browser), so the
  // dev session marker and the group override degrade to "not set" instead of throwing.
  private get sessionStore(): Nullable<Storage> {
    return 'sessionStorage' in this.window ? this.window.sessionStorage : null;
  }

  private get localStore(): Nullable<Storage> {
    return 'localStorage' in this.window ? this.window.localStorage : null;
  }
}
